import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";
import useJunctionList from "./data/useJunctionList";
import useJunctionGeom from "./data/useJunctionGeom";
import useJunctionChartData from "./data/useJunctionChartData";
import useApproachChartData from "./data/useApproachChartData";
import useExitChartData from "./data/useExitChartData";
import useExitIds from "./data/useExitIds";
import JunctionsMap from "./components/JunctionsMap";
import JunctionsChartsPanel from "./components/JunctionsChartsPanel";
import metricOptions from "./data/metricOptions.json";
import getFeatureName from "./featureName";
import { formatDateDQ, getUTCDatesFromTo, getUTCDatesFromParams } from "modules/formatDates";

function Junctions() {
  const { t } = useTranslation();
  const { junctions, loading: junctionsLoading } = useJunctionList();
  const [selectedId, setSelectedId] = useState(null);

  const options = useMemo(
    () => junctions.map((j) => ({ id: j.id, label: j.nameJunction })),
    [junctions]
  );

  const {
    approachGeojson,
    exitLineGeojson,
    exitArrowGeojson,
    loading: geomLoading,
  } = useJunctionGeom(selectedId);

  const approachOptions = useMemo(() => {
    const byId = new Map();
    (approachGeojson?.features ?? []).forEach((f) => {
      const id = f.properties?.id;
      if (id === undefined || id === null) return;
      if (!byId.has(id) || byId.get(id) == null) byId.set(id, getFeatureName(f.properties));
    });
    return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.id - b.id);
  }, [approachGeojson]);

  const [timeType, setTimeType] = useState("24h");
  const now = new Date();
  const [customDates, setCustomDates] = useState({
    from: formatDateDQ(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
    to: formatDateDQ(now),
  });
  const [customHours, setCustomHours] = useState(2);
  const [selectedMetric, setSelectedMetric] = useState(-1);
  const { chartsData, loading: chartsLoading, fetchChartData } = useJunctionChartData();

  const {
    chartsData: approachChartsData,
    loading: approachChartsLoading,
    fetchApproachChartData,
  } = useApproachChartData();

  const [ddSelectedApproachId, setDdSelectedApproachId] = useState(null);
  const {
    chartsData: ddApproachChartsData,
    loading: ddApproachChartsLoading,
    fetchExitChartData,
  } = useExitChartData();

  const { exitIds: highlightedExitIds, fetchExitIds, clearExitIds } = useExitIds();

  const [exitTimeType, setExitTimeType] = useState("24h");
  const [exitCustomDates, setExitCustomDates] = useState({
    from: formatDateDQ(new Date(now.getTime() - 24 * 60 * 60 * 1000)),
    to: formatDateDQ(now),
  });
  const [exitCustomHours, setExitCustomHours] = useState(2);

  const [activeGroup, setActiveGroup] = useState(null); // null | "overview" | "exit"
  const [tabIndex, setTabIndex] = useState(0);
  const hasAnyPanel = activeGroup != null;

  useEffect(() => {
    setDdSelectedApproachId(null);
  }, [selectedId]);

  useEffect(() => {
    if (ddSelectedApproachId == null) {
      clearExitIds();
      return;
    }
    fetchExitIds(ddSelectedApproachId);
  }, [ddSelectedApproachId, fetchExitIds, clearExitIds]);

  const handleApproachClick = (approachId) => {
    setDdSelectedApproachId(approachId);
  };

  const handleDdApproachChange = (e) => {
    const value = e.target.value;
    setDdSelectedApproachId(value === "" ? null : value);
  };

  const exitApplyEnabled = () => {
    if (selectedId == null || ddSelectedApproachId == null) return false;
    if (exitTimeType === "customH") return exitCustomHours >= 0;
    if (exitTimeType === "customD") return new Date(exitCustomDates.to) >= new Date(exitCustomDates.from);
    return true;
  };

  const handleExitApply = () => {
    let dates = { from: "", to: "" };
    let casted;
    switch (exitTimeType) {
      case "12h":
        casted = getUTCDatesFromTo(12);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "24h":
        casted = getUTCDatesFromTo(24);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "customH":
        casted = getUTCDatesFromTo(exitCustomHours);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "customD":
        casted = getUTCDatesFromParams(exitCustomDates.from, exitCustomDates.to);
        dates.from = casted.date1UTC;
        dates.to = casted.date2UTC;
        break;
      default:
        casted = getUTCDatesFromTo(1);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
    }

    setActiveGroup("exit");
    setTabIndex(0);
    fetchApproachChartData({
      junctionId: selectedId,
      approachId: ddSelectedApproachId,
      from: dates.from,
      to: dates.to,
    });
    fetchExitChartData({
      approachId: ddSelectedApproachId,
      from: dates.from,
      to: dates.to,
    });
  };

  const metricLabel = metricOptions.find((m) => m.id === selectedMetric)?.label ?? "";

  const handleChangeTimeType = (e) => setTimeType(e.target.value);

  const applyEnabled = () => {
    if (selectedMetric === -1) return false;
    if (timeType === "customH") return customHours >= 0;
    if (timeType === "customD") return new Date(customDates.to) >= new Date(customDates.from);
    return true;
  };

  const handleApply = () => {
    let dates = { from: "", to: "" };
    let casted;
    switch (timeType) {
      case "12h":
        casted = getUTCDatesFromTo(12);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "24h":
        casted = getUTCDatesFromTo(24);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "customH":
        casted = getUTCDatesFromTo(customHours);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
      case "customD":
        casted = getUTCDatesFromParams(customDates.from, customDates.to);
        dates.from = casted.date1UTC;
        dates.to = casted.date2UTC;
        break;
      default:
        casted = getUTCDatesFromTo(1);
        dates.from = casted.hourAgoUTC;
        dates.to = casted.nowUTC;
        break;
    }

    setActiveGroup("overview");
    setTabIndex(0);
    fetchChartData({
      junctionId: selectedId,
      metric: selectedMetric,
      from: dates.from,
      to: dates.to,
    });
  };

  return (
    <DashboardLayout>
      <MDBox mt={2}>
        <Grid container spacing={2} alignItems="stretch">
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 2, height: "100%" }}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                <FormControl fullWidth>
                  <InputLabel id="junctions-junction-label">{t("junctions.labels.junction")}</InputLabel>
                  <Select
                    labelId="junctions-junction-label"
                    id="junctions-junction"
                    value={selectedId ?? ""}
                    label={t("junctions.labels.junction")}
                    disabled={junctionsLoading}
                    onChange={(e) => setSelectedId(e.target.value === "" ? null : e.target.value)}
                    sx={{ height: 56, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                  >
                    {options.map((o) => (
                      <MenuItem key={o.id} value={o.id}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="junctions-timeframe-label">{t("junctions.labels.timeframe")}</InputLabel>
                  <Select
                    labelId="junctions-timeframe-label"
                    id="junctions-timeframe"
                    value={timeType}
                    label={t("junctions.labels.timeframe")}
                    onChange={handleChangeTimeType}
                    sx={{ height: 56, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                  >
                    <MenuItem value="1h">{t("junctions.timeframeOptions.last1Hour")}</MenuItem>
                    <MenuItem value="12h">{t("junctions.timeframeOptions.last12Hours")}</MenuItem>
                    <MenuItem value="24h">{t("junctions.timeframeOptions.last24Hours")}</MenuItem>
                    <MenuItem value="customH">{t("junctions.timeframeOptions.customHours")}</MenuItem>
                    <MenuItem value="customD">{t("junctions.timeframeOptions.customDates")}</MenuItem>
                  </Select>
                </FormControl>

                {timeType === "customD" && (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    <MDDatePicker
                      input={{ placeholder: "dd/mm/yyyy HH:mm" }}
                      value={customDates.from ? new Date(customDates.from) : null}
                      options={{
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                        altFormat: "d/m/Y H:i",
                        time_24hr: true,
                      }}
                      onChange={(date) => {
                        const picked = Array.isArray(date) ? date[0] : date;
                        setCustomDates((prev) => ({
                          ...prev,
                          from: picked ? picked.toISOString() : "",
                        }));
                      }}
                    />
                    <MDDatePicker
                      input={{ placeholder: "dd/mm/yyyy HH:mm" }}
                      value={customDates.to ? new Date(customDates.to) : null}
                      options={{
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                        altFormat: "d/m/Y H:i",
                        time_24hr: true,
                      }}
                      onChange={(date) => {
                        const picked = Array.isArray(date) ? date[0] : date;
                        setCustomDates((prev) => ({
                          ...prev,
                          to: picked ? picked.toISOString() : "",
                        }));
                      }}
                    />
                  </MDBox>
                )}

                {timeType === "customH" && (
                  <TextField
                    id="junctions-custom-hours"
                    label={t("junctions.labels.lastHours")}
                    type="number"
                    value={customHours}
                    onChange={(e) => setCustomHours(parseInt(e.target.value, 10))}
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { height: 56 } }}
                  />
                )}

                <FormControl fullWidth>
                  <InputLabel id="junctions-metric-label">{t("junctions.labels.metric")}</InputLabel>
                  <Select
                    labelId="junctions-metric-label"
                    id="junctions-metric"
                    value={selectedMetric}
                    label={t("junctions.labels.metric")}
                    onChange={(e) => setSelectedMetric(e.target.value)}
                    sx={{ height: 56, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                  >
                    <MenuItem value={-1}>{t("common.buttons.none")}</MenuItem>
                    {metricOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleApply}
                  disabled={!applyEnabled()}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {t("common.buttons.apply")}
                </MDButton>

                <Divider sx={{ my: 1 }} />

                <MDTypography variant="button" fontWeight="medium" color="text">
                  {t("junctions.exitsDetails")}
                </MDTypography>

                <FormControl fullWidth>
                  <InputLabel id="junctions-dd-approach-label">{t("junctions.labels.approach")}</InputLabel>
                  <Select
                    labelId="junctions-dd-approach-label"
                    id="junctions-dd-approach"
                    value={ddSelectedApproachId ?? ""}
                    label={t("junctions.labels.approach")}
                    disabled={selectedId == null || geomLoading || approachOptions.length === 0}
                    onChange={handleDdApproachChange}
                    sx={{ height: 56, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                  >
                    <MenuItem value="">{t("common.buttons.none")}</MenuItem>
                    {approachOptions.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name != null && name !== ""
                          ? `${name} ${id}`
                          : t("junctions.approachOption", { id })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="junctions-exit-timeframe-label">{t("junctions.labels.timeframe")}</InputLabel>
                  <Select
                    labelId="junctions-exit-timeframe-label"
                    id="junctions-exit-timeframe"
                    value={exitTimeType}
                    label={t("junctions.labels.timeframe")}
                    onChange={(e) => setExitTimeType(e.target.value)}
                    sx={{ height: 56, "& .MuiSelect-select": { display: "flex", alignItems: "center" } }}
                  >
                    <MenuItem value="1h">{t("junctions.timeframeOptions.last1Hour")}</MenuItem>
                    <MenuItem value="12h">{t("junctions.timeframeOptions.last12Hours")}</MenuItem>
                    <MenuItem value="24h">{t("junctions.timeframeOptions.last24Hours")}</MenuItem>
                    <MenuItem value="customH">{t("junctions.timeframeOptions.customHours")}</MenuItem>
                    <MenuItem value="customD">{t("junctions.timeframeOptions.customDates")}</MenuItem>
                  </Select>
                </FormControl>

                {exitTimeType === "customD" && (
                  <MDBox display="flex" flexDirection="column" gap={1}>
                    <MDDatePicker
                      input={{ placeholder: "dd/mm/yyyy HH:mm" }}
                      value={exitCustomDates.from ? new Date(exitCustomDates.from) : null}
                      options={{
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                        altFormat: "d/m/Y H:i",
                        time_24hr: true,
                      }}
                      onChange={(date) => {
                        const picked = Array.isArray(date) ? date[0] : date;
                        setExitCustomDates((prev) => ({
                          ...prev,
                          from: picked ? picked.toISOString() : "",
                        }));
                      }}
                    />
                    <MDDatePicker
                      input={{ placeholder: "dd/mm/yyyy HH:mm" }}
                      value={exitCustomDates.to ? new Date(exitCustomDates.to) : null}
                      options={{
                        enableTime: true,
                        dateFormat: "d/m/Y H:i",
                        altFormat: "d/m/Y H:i",
                        time_24hr: true,
                      }}
                      onChange={(date) => {
                        const picked = Array.isArray(date) ? date[0] : date;
                        setExitCustomDates((prev) => ({
                          ...prev,
                          to: picked ? picked.toISOString() : "",
                        }));
                      }}
                    />
                  </MDBox>
                )}

                {exitTimeType === "customH" && (
                  <TextField
                    id="junctions-exit-custom-hours"
                    label={t("junctions.labels.lastHours")}
                    type="number"
                    value={exitCustomHours}
                    onChange={(e) => setExitCustomHours(parseInt(e.target.value, 10))}
                    fullWidth
                    sx={{ "& .MuiInputBase-root": { height: 56 } }}
                  />
                )}

                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleExitApply}
                  disabled={!exitApplyEnabled()}
                  fullWidth
                  sx={{ height: 56 }}
                >
                  {t("common.buttons.apply")}
                </MDButton>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%", minHeight: 400 }}>
              <JunctionsMap
                approachSource={approachGeojson}
                exitLineSource={exitLineGeojson}
                exitArrowSource={exitArrowGeojson}
                selectedExitIds={highlightedExitIds}
                selectedApproachId={ddSelectedApproachId}
                onApproachClick={handleApproachClick}
              />
            </Card>
          </Grid>
        </Grid>

        {hasAnyPanel && (
          <Card sx={{ mt: 2 }}>
            <Tabs
              value={tabIndex}
              onChange={(e, newIndex) => setTabIndex(newIndex)}
              indicatorColor="info"
              textColor="info"
              variant="fullWidth"
            >
              {activeGroup === "overview" && <Tab label={t("junctions.tabs.junctionOverview")} />}
              {activeGroup === "exit" && (
                <Tab
                  label={
                    ddSelectedApproachId != null
                      ? t("junctions.tabs.approachMetrics", { id: ddSelectedApproachId })
                      : t("junctions.tabs.metrics")
                  }
                />
              )}
              {activeGroup === "exit" && (
                <Tab
                  label={
                    ddSelectedApproachId != null
                      ? t("junctions.tabs.approachExits", { id: ddSelectedApproachId })
                      : t("junctions.tabs.exits")
                  }
                />
              )}
            </Tabs>
          </Card>
        )}

        {activeGroup === "overview" && (
          <JunctionsChartsPanel chartsData={chartsData} loading={chartsLoading} metricLabel={metricLabel} />
        )}
        {activeGroup === "exit" && tabIndex === 0 && (
          <JunctionsChartsPanel
            chartsData={approachChartsData}
            loading={approachChartsLoading}
            metricLabel={t("junctions.tabs.approachMetrics", { id: ddSelectedApproachId })}
          />
        )}
        {activeGroup === "exit" && tabIndex === 1 && (
          <JunctionsChartsPanel
            chartsData={ddApproachChartsData}
            loading={ddApproachChartsLoading}
            metricLabel={t("junctions.tabs.approachExits", { id: ddSelectedApproachId })}
            percent
          />
        )}
      </MDBox>
    </DashboardLayout>
  );
}

export default Junctions;
